import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { IdCard, LogOut } from "lucide-react";
import { adminApi, errorMessage } from "../lib/api";
import { PhoneVerify } from "../components/landing/PhoneVerify";
import { WorkerJobs } from "../components/worker/WorkerJobs";
import { Joinings } from "../components/worker/Joinings";
import { TodayJobs } from "../components/worker/TodayJobs";
import { TelegramUpdates } from "../components/hk/TelegramUpdates";
import { MySlip } from "../components/worker/MySlip";
import { MyRatings } from "../components/worker/MyRatings";
import { Vacancies } from "../components/worker/Vacancies";

const Login = ({ onLogin }) => {
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  useEffect(() => {
    if (!token) return;
    adminApi.post("/worker/login", { phone, phone_verification_token: token })
      .then((r) => onLogin(r.data)).catch((e) => { toast.error(errorMessage(e)); setToken(""); });
  }, [token, phone, onLogin]);
  return (
    <div data-testid="worker-login" className="mx-auto mt-16 max-w-md space-y-5 border border-white/10 bg-[#1E293B]/60 p-8">
      <h1 className="font-heading text-2xl font-extrabold text-white">Employee login</h1>
      <p className="text-sm text-slate-400">Use the mobile number you registered with. We'll send an SMS OTP.</p>
      <PhoneVerify prefix="worker-login" label="Mobile Number" phone={phone} token={token}
        onPhone={(v) => { setPhone(v); setToken(""); }} onToken={setToken} />
    </div>
  );
};

const IdCardLink = () => {
  const open = () => adminApi.get("/worker/id-card").then((r) => { window.location.href = `/id/${r.data.token}`; }).catch((e) => toast.error(errorMessage(e)));
  return (
    <button onClick={open} data-testid="worker-id-card-button" className="flex items-center gap-2 bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">
      <IdCard className="h-4 w-4" />Download ID Card
    </button>
  );
};

const Profile = ({ w }) => (
  <div data-testid="worker-profile" className="space-y-2">
    <h1 data-testid="worker-profile-name" className="font-heading text-3xl font-extrabold text-white">{w.full_name}</h1>
    <p className="text-sm text-slate-400">{w.job_role} · {w.district}, {w.state} · <span className="font-mono">{w.reference}</span></p>
    <p data-testid="worker-kyc-status" className={`inline-block px-2 py-0.5 text-xs font-semibold ${w.kyc_status === "approved" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
      KYC {w.kyc_status === "approved" ? "approved: employers can see your profile" : `${w.kyc_status}: employers see you after approval`}
    </p>
    {w.kyc_status === "approved" && <IdCardLink />}
  </div>
);

export default function WorkerPortal() {
  const [w, setW] = useState(undefined);
  const load = useCallback(() => {
    adminApi.get("/worker/me").then((r) => setW(r.data)).catch(() => setW(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const logout = () => adminApi.post("/worker/logout").finally(() => setW(null));
  return (
    <div className="min-h-screen bg-[#0B1120] px-4 pb-20 font-body text-slate-100 sm:px-6">
      <header className="mx-auto flex max-w-6xl items-center justify-between py-5">
        <a href="/" data-testid="worker-portal-home" className="flex items-center gap-2.5">
          <span className="font-heading text-lg font-extrabold text-white">Balangir Group <span className="font-mono text-xs font-normal text-slate-500">/ employee</span></span>
        </a>
        {w && (
          <button onClick={logout} data-testid="worker-logout-button" className="flex items-center gap-2 text-sm text-slate-300 hover:text-orange-400">
            <LogOut className="h-4 w-4" />Log out
          </button>
        )}
      </header>
      {w === null && <Login onLogin={setW} />}
      {w && (
        <main className="mx-auto max-w-6xl space-y-10">
          <Profile w={w} />
          <TelegramUpdates base="/worker/hk/telegram" tid="worker-telegram" what="new job alerts and reminders" />
          <TodayJobs />
          <MySlip />
          <MyRatings />
          <Joinings />
          <WorkerJobs />
          <Vacancies />
        </main>
      )}
    </div>
  );
}
